import { useEffect, useState } from "react";
import { useFormik } from "formik";
import AddressModal from "./AddressModal";
import CouponModal from "./CouponModal";
import api from "../../shared/utils/request";
import { getDayName } from "../../shared/utils/formatters";
import PageLoader from "../shared/pageLoader";
import { usePageSeo } from "../../shared/seo";
import LoginWarning, { EmptyCart } from "../shared/warningPages";
import MobileCheckoutBar from "../shared/mobileCheckoutBar/MobileCheckoutBar";
import toast from "react-hot-toast";
import { useNavigate } from "react-router-dom";
import {
  loadRazorpayScript,
  initiateRazorpayPayment,
} from "../../shared/utils/razorpay";

const CheckoutPage = () => {
  usePageSeo("checkout");
  const navigate = useNavigate();

  const [cartDatas, setCartDatas] = useState([]);
  const [cartLoading, setCartLoading] = useState(false);
  const [deliverySlots, setDeliverySlots] = useState([]);
  const [deliverySlotsLoading, setDeliverySlotsLoading] = useState(false);
  const [availableCoupons, setAvailableCoupons] = useState([]);
  const [addresses, setAddresses] = useState([]);
  const [addressLoading, setAddressLoading] = useState(false);
  const [showCouponList, setShowCouponList] = useState(false);
  const [showAddAddress, setShowAddAddress] = useState(false);
  const [applyCouponLoading, setApplyCouponLoading] = useState(false);
  const [checkoutLoading, setCheckoutLoading] = useState(false);
  const [cartTotal, setCartTotal] = useState(0);
  const isToken = localStorage.getItem("token");

  const formik = useFormik({
    initialValues: {
      selectedTimeSlot: "",
      couponCode: "",
      appliedCoupon: null,
      notes: "",
      selectedAddress: "",
      paymentMethod: "ONLINE",
    },
    onSubmit: async (values) => {
      try {
        setCheckoutLoading(true);
        const data = await api.post("customer/createOrder", {
          delivery_slot: values?.selectedTimeSlot?.label,
          payment_type: values?.paymentMethod,
          delivery_date: values?.selectedTimeSlot?.date,
          coupon_code: values?.appliedCoupon?.coupon_code,
          deliveryNote: values?.notes,
        });
        console.log("datadata", data);
        if (!data?.status) {
          toast.error(data?.message);
          setCheckoutLoading(false);
          return;
        }

        if (values?.paymentMethod === "ONLINE") {
          const scriptLoaded = await loadRazorpayScript();
          if (!scriptLoaded) {
            toast.error("Failed to load payment gateway. Please try again.");
            setCheckoutLoading(false);
            return;
          }

          initiateRazorpayPayment({
            amount: formik.values.appliedCoupon
              ? cartTotal?.cart_total_amount -
                formik.values.appliedCoupon?.discount
              : cartTotal?.cart_total_amount,
            orderId: data?.razorpay_order_id,
            onSuccess: async (response) => {
              try {
                const verifyData = await api.post("customer/verifyPayment", {
                  razorpay_order_id: response.razorpay_order_id,
                  razorpay_payment_id: response.razorpay_payment_id,
                  razorpay_signature: response.razorpay_signature,
                  delivery_slot: values?.selectedTimeSlot?.label,
                  payment_type: values?.paymentMethod,
                  delivery_date: values?.selectedTimeSlot?.date,
                });
                if (verifyData?.status) {
                  navigate("/success");
                } else {
                  toast.error(
                    verifyData?.message || "Payment verification failed",
                  );
                }
              } catch (err) {
                toast.error("Payment verification failed");
              } finally {
                setCheckoutLoading(false);
              }
            },
            onFailure: (error) => {
              toast.error(error || "Payment failed");
              setCheckoutLoading(false);
            },
          });
        } else {
          navigate("/success");
          setCheckoutLoading(false);
        }
      } catch (error) {
        setCheckoutLoading(false);
        console.error("Failed to place order:", error);
      } finally {
        setCheckoutLoading(false);
      }
    },
  });

  const handleApplyCoupon = async (couponCode) => {
    const code = couponCode || formik.values.couponCode;
    try {
      setApplyCouponLoading(true);
      const data = await api.post("coupons/validate", {
        code: code,
        order_amount: String(cartTotal?.cart_total_amount),
      });
      if (data?.status) {
        formik.setFieldValue("appliedCoupon", {
          coupon_id: data?.coupon_id,
          discount: data?.discount,
          coupon_code: code,
        });
        formik.setFieldValue("couponCode", code);
        setShowCouponList(false);
      } else {
        toast.error(data?.message);
      }
      setApplyCouponLoading(false);
    } catch (error) {
      setApplyCouponLoading(false);
      console.error("Failed to apply coupon:", error);
    } finally {
      setApplyCouponLoading(false);
    }
  };

  const handleRemoveCoupon = () => {
    formik.setFieldValue("appliedCoupon", null);
    formik.setFieldValue("couponCode", "");
  };

  useEffect(() => {
    const getCartDatas = async () => {
      setCartLoading(true);
      try {
        const data = await api.post("customer/getCartList");
        setCartDatas(data?.cartItems);
        setCartTotal({
          cart_sub_total_amount: data?.cart_sub_total_amount,
          cart_total_amount: data?.cart_total_amount,
          total_tax: data?.total_tax,
        });
      } catch (error) {
        console.error("Failed to fetch cart data:", error);
      } finally {
        setCartLoading(false);
      }
    };

    getCartDatas();
  }, []);

  const handleSaveAddress = () => {
    getAddresses();
  };

  useEffect(() => {
    const getDeliverySlots = async () => {
      setDeliverySlotsLoading(true);
      try {
        const data = await api.get("customer/getDeliverySlots");
        if (data?.available_shifts?.length) {
          formik.setFieldValue("selectedTimeSlot", data.available_shifts[0]);
          setDeliverySlots(data?.available_shifts);
        }
      } catch (error) {
        console.error("Failed to fetch delivery slots:", error);
      } finally {
        setDeliverySlotsLoading(false);
      }
    };

    getDeliverySlots();
  }, []);

  useEffect(() => {
    getAddresses();
  }, []);

  useEffect(() => {
    const getCoupons = async () => {
      try {
        const data = await api.get("customer/active-coupons");
        if (data) {
          setAvailableCoupons(data?.data);
        }
      } catch (error) {
        console.error("Failed to fetch coupons:", error);
      }
    };

    getCoupons();
  }, []);

  const getAddresses = async () => {
    setAddressLoading(true);
    try {
      const data = await api.get("customer/addresses?defaultOnly=true");
      if (data) {
        setAddresses(data);
      }
      setAddressLoading(false);
    } catch (error) {
      console.error("Failed to fetch addresses:", error);
    } finally {
      setAddressLoading(false);
    }
  };

  const defaultAddress = addresses?.[0] || null;

  const renderCouponSection = () => (
    <div className="bg-gradient-to-r from-main-50 to-purple-50 rounded-12 px-24 py-20 mb-24 border-2 border-dashed border-main-300">
      {!formik.values.appliedCoupon ? (
        <>
          <div className="flex-align gap-12 mb-12">
            <i className="ph-fill ph-ticket text-main-600 text-xl" />
            <span className="text-md fw-semibold text-gray-900">
              Apply Coupon
            </span>
          </div>
          <div className="flex-align gap-8 mb-12">
            <input
              type="text"
              name="couponCode"
              value={formik.values.couponCode}
              onChange={(e) =>
                formik.setFieldValue("couponCode", e.target.value)
              }
              className="common-input border-main-200 flex-grow-1 py-8 text-sm"
              placeholder="Enter Coupon Code"
            />
            <button
              onClick={() => handleApplyCoupon()}
              disabled={applyCouponLoading || !formik.values.couponCode}
              className="btn btn-main px-20 py-8 rounded-8 text-sm text-nowrap"
            >
              {applyCouponLoading ? (
                <span
                  className="spinner-border spinner-border-sm"
                  aria-hidden="true"
                ></span>
              ) : (
                "Apply"
              )}
            </button>
          </div>
          <button
            onClick={() => setShowCouponList(true)}
            className="text-sm text-main-600 hover-text-main-700 fw-semibold bg-transparent border-0 p-0 w-100 text-center"
          >
            <i className="ph ph-gift me-1" />
            View Available Coupons
          </button>
        </>
      ) : (
        <div>
          <div className="flex-align gap-8 mb-8">
            <i className="ph-fill ph-check-circle text-success-600 text-lg" />
            <span className="text-sm fw-bold text-gray-900 font-mono">
              {formik.values.couponCode}
            </span>
            <span className="text-xs text-success-600 fw-semibold">
              Applied!
            </span>
          </div>
          <button
            onClick={handleRemoveCoupon}
            className="text-xs text-danger-600 hover-text-danger-700 fw-semibold bg-transparent border-0 p-0"
          >
            <i className="ph ph-x me-1" />
            Remove Coupon
          </button>
        </div>
      )}
    </div>
  );

  const payableTotal = formik.values.appliedCoupon
    ? (
        cartTotal?.cart_total_amount - formik.values.appliedCoupon?.discount
      ).toFixed(2)
    : cartTotal?.cart_total_amount;

  if (!isToken && !cartLoading && !addressLoading && !deliverySlotsLoading) {
    return <LoginWarning />;
  }

  if (
    !cartLoading &&
    !addressLoading &&
    !deliverySlotsLoading &&
    cartDatas?.length === 0
  ) {
    return <EmptyCart />;
  }

  return (
    <>
      {(cartLoading || addressLoading || deliverySlotsLoading) && (
        <PageLoader />
      )}
      <section className="checkout py-80 page-with-mobile-checkout">
        <div className="container container-lg">
          <div className="row">
            <div className="col-xl-9 col-lg-8">
              <div className="pe-xl-5">
                {/* Coupon Section - Mobile First */}
                <div className="d-lg-none mb-16">{renderCouponSection()}</div>

                {/* Delivery Address Section */}
                <div className="mb-40">
                  <div className="flex-between flex-wrap gap-12 mb-24">
                    <h5 className="text-xl fw-semibold">Delivery Address</h5>
                    <button
                      onClick={() => setShowAddAddress(!showAddAddress)}
                      className="btn btn-outline-main px-20 py-8 rounded-8 text-sm text-nowrap"
                    >
                      <i className="ph ph-plus me-2" />
                      Add Address
                    </button>
                  </div>

                  {defaultAddress ? (
                    <div className="border border-main-600 bg-main-50 rounded-8 p-20 transition-all">
                      <div className="flex-between flex-wrap gap-12">
                        <div className="flex-align gap-12 min-w-0 flex-grow-1">
                          <div className="w-40 h-40 bg-main-600 text-white rounded-8 flex-center flex-shrink-0">
                            <i className="ph ph-house text-lg" />
                          </div>
                          <div className="min-w-0">
                            <div className="flex-align gap-8 flex-wrap mb-4">
                              <span className="fw-semibold text-md text-gray-900">
                                {defaultAddress.name}
                              </span>
                              <span className="badge bg-main-600 text-white text-xs px-8 py-4">
                                Default
                              </span>
                            </div>
                            <p className="text-sm text-gray-700 mb-0">
                              {defaultAddress.address_line_1},{" "}
                              {defaultAddress.city}, {defaultAddress.state} -{" "}
                              {defaultAddress.pincode}
                            </p>
                            <p className="text-sm text-gray-600 mb-0 mt-4">
                              <i className="ph ph-phone me-2" />
                              {defaultAddress.phone}
                            </p>
                          </div>
                        </div>
                        <button
                          onClick={() => navigate("/address-book")}
                          className="btn btn-outline-main px-16 py-8 rounded-8 text-sm text-nowrap"
                        >
                          <i className="ph ph-pencil me-2" />
                          Change
                        </button>
                      </div>
                    </div>
                  ) : (
                    <div className="border border-dashed border-gray-200 rounded-8 p-32 text-center">
                      <i className="ph ph-map-pin text-4xl text-neutral-300 mb-16 d-block" />
                      <p className="text-gray-600 mb-16">
                        No default delivery address found.
                      </p>
                      <button
                        onClick={() => navigate("/address-book")}
                        className="btn btn-main px-24 py-8 rounded-8 text-sm"
                      >
                        <i className="ph ph-plus me-2" />
                        Add Address
                      </button>
                    </div>
                  )}
                </div>

                <AddressModal
                  show={showAddAddress}
                  onClose={() => setShowAddAddress(false)}
                  onSaveAddress={handleSaveAddress}
                />

                {/* Time Slot Selection */}
                <div className="mb-40">
                  <h5 className="text-xl fw-semibold mb-24">
                    Select Delivery Time
                  </h5>
                  <div className="row g-3 flex-nowrap flex-sm-wrap overflow-x-auto pb-2">
                    {deliverySlots?.map((slot, index) => (
                      <div
                        key={"slots-" + index}
                        className="col-6 col-sm-6 col-md-3"
                      >
                        <div
                          onClick={() =>
                            formik.setFieldValue("selectedTimeSlot", slot)
                          }
                          className={`border rounded-8 p-16 text-center cursor-pointer transition-all ${
                            formik.values.selectedTimeSlot === slot
                              ? "border-main-600 bg-main-600 text-white"
                              : "border-gray-100 hover-border-main-300"
                          }`}
                        >
                          <i className="ph ph-clock text-2xl mb-8" />
                          <p className="text-sm fw-medium mb-0">
                            {getDayName(slot?.date)} {slot?.label}
                          </p>
                          {/* <p className="text-sm fw-medium mb-0">
                            {slot?.start} - {slot?.end}
                          </p> */}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Additional Information */}
                <div>
                  <h5 className="text-xl fw-semibold mb-24">
                    Additional Information
                  </h5>
                  <textarea
                    name="notes"
                    value={formik.values.notes}
                    onChange={formik.handleChange}
                    className="common-input border-gray-100"
                    rows="4"
                    placeholder="Notes about your order, e.g. special notes for delivery."
                  />
                </div>
              </div>
            </div>
            <div className="col-xl-3 col-lg-4">
              <div className="checkout-sidebar">
                {/* Coupon Section */}
                <div className="d-none d-lg-block">{renderCouponSection()}</div>

                <div className="border border-gray-100 rounded-8 px-16 py-20 mt-24">
                  <div className="mb-16 pb-12 border-bottom border-gray-100 flex-between gap-8">
                    <span className="text-gray-900 fw-medium text-md font-heading-two">
                      Product
                    </span>
                    <span className="text-gray-900 fw-medium text-md font-heading-two">
                      Total
                    </span>
                  </div>
                  <div style={{ maxHeight: "200px", overflowY: "auto" }}>
                    {cartDatas?.map((cartItem, index) => (
                      <div className="flex-between gap-8 mb-16" key={index}>
                        <span
                          className="text-gray-900 fw-normal text-sm font-heading-two"
                          style={{
                            flex: 1,
                            minWidth: 0,
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap",
                          }}
                        >
                          {cartItem?.title}
                        </span>
                        <div className="flex-align gap-12">
                          <span
                            className="text-gray-500 fw-normal text-sm font-heading-two"
                            style={{ width: "60px", textAlign: "right" }}
                          >
                            × {cartItem?.quantity}
                          </span>
                          <span
                            className="text-gray-900 fw-bold text-sm font-heading-two"
                            style={{ width: "72px", textAlign: "right" }}
                          >
                            ₹{cartItem?.sub_total_amount}
                          </span>
                        </div>
                      </div>
                    ))}
                  </div>
                  <div className="border-top border-gray-100 pt-16 mt-16">
                    <div className="mb-12 flex-between gap-8">
                      <span className="text-gray-900 font-heading-two text-sm">
                        Subtotal
                      </span>
                      <span className="text-gray-900 font-heading-two text-sm fw-bold">
                        ₹{cartTotal?.cart_sub_total_amount}
                      </span>
                    </div>
                    <div className="mb-12 flex-between gap-8">
                      <span className="text-gray-900 font-heading-two text-sm">
                        Total Tax
                      </span>
                      <span className="text-gray-900 font-heading-two text-sm fw-bold">
                        ₹{cartTotal?.total_tax}
                      </span>
                    </div>
                    <div className="mb-12 flex-between gap-8">
                      <span className="text-gray-900 font-heading-two text-sm">
                        Shipping Charge
                      </span>
                      <span className="text-gray-900 font-heading-two text-sm fw-bold">
                        Free
                      </span>
                    </div>
                    {formik.values.appliedCoupon && (
                      <div className="mb-12 flex-between gap-8">
                        <span className="text-success font-heading-two text-sm">
                          Discount ({formik.values.couponCode})
                        </span>
                        <span className="text-success font-heading-two text-sm fw-bold">
                          ₹{formik.values.appliedCoupon?.discount}
                        </span>
                      </div>
                    )}
                    <div className="border-top border-gray-100 pt-12 mt-12">
                      <div className="flex-between gap-8">
                        <span className="text-gray-900 font-heading-two text-lg fw-semibold">
                          Total
                        </span>
                        <span className="text-main-600 font-heading-two text-lg fw-bold">
                          ₹{payableTotal}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
                <div className="mt-32 mb-32">
                  <div className="payment-item">
                    <div className="form-check common-check common-radio py-16 mb-0">
                      <input
                        className="form-check-input"
                        type="radio"
                        name="paymentMethod"
                        id="payment2"
                        value="ONLINE"
                        checked={formik.values.paymentMethod === "ONLINE"}
                        onChange={formik.handleChange}
                      />
                      <label
                        className="form-check-label fw-semibold text-neutral-600"
                        htmlFor="payment2"
                      >
                        Online Payment (Razorpay)
                      </label>
                    </div>
                  </div>
                  <div className="payment-item">
                    <div className="form-check common-check common-radio py-16 mb-0">
                      <input
                        className="form-check-input"
                        type="radio"
                        name="paymentMethod"
                        id="payment3"
                        value="COD"
                        checked={formik.values.paymentMethod === "COD"}
                        onChange={formik.handleChange}
                      />
                      <label
                        className="form-check-label fw-semibold text-neutral-600"
                        htmlFor="payment3"
                      >
                        Cash on delivery
                      </label>
                    </div>
                  </div>
                </div>
                <button
                  onClick={formik.handleSubmit}
                  disabled={checkoutLoading}
                  className="btn btn-main  py-18 w-100 rounded-8 mt-20 d-none d-lg-block"
                >
                  {checkoutLoading ? (
                    <span
                      className="spinner-border spinner-border-sm"
                      aria-hidden="true"
                    ></span>
                  ) : (
                    "Place Order"
                  )}
                </button>
              </div>
            </div>
          </div>

          <MobileCheckoutBar
            total={payableTotal}
            buttonLabel="Place Order"
            onClick={formik.handleSubmit}
            loading={checkoutLoading}
            disabled={checkoutLoading}
            paymentOptions={[
              {
                value: "ONLINE",
                label: "Online Payment (Razorpay)",
                icon: "ph ph-credit-card",
              },
              {
                value: "COD",
                label: "Cash on Delivery",
                icon: "ph ph-money",
              },
            ]}
            selectedPayment={formik.values.paymentMethod}
            onPaymentChange={(value) =>
              formik.setFieldValue("paymentMethod", value)
            }
          />

          <CouponModal
            show={showCouponList}
            onClose={() => setShowCouponList(false)}
            coupons={availableCoupons}
            onApplyCoupon={handleApplyCoupon}
          />
        </div>
      </section>
    </>
  );
};

export default CheckoutPage;
